import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import Icon from '../../components/AppIcon';

const MyTasksPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all'); // all, overdue, in_progress, todo, completed
  const [sortBy, setSortBy] = useState('priority'); // priority, dueDate, status

  // Load tasks from API
  const loadTasks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { default: realApiService } = await import('../../utils/realApiService.js');
      const result = await realApiService.taskAssignment.getMyAssignedTasks();

      const list = Array.isArray(result?.tasks) ? result.tasks : Array.isArray(result) ? result : [];
      const mapped = list.map(t => ({
        id: t.id,
        title: t.title || 'Untitled Task',
        description: t.description || '',
        priority: t.priority || 'medium',
        status: t.status || 'todo',
        dueDate: t.due_date || t.dueDate || null,
        project: t.project?.name || t.project_name || null,
        projectId: t.project?.id || t.project_id || null,
        createdAt: t.created_at || t.createdAt,
        updatedAt: t.updated_at || t.updatedAt,
      }));
      setTasks(mapped);
    } catch (e) {
      setError(e.message || 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Priority configuration
  const priorityConfig = {
    urgent: { order: 1, icon: 'AlertCircle', color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Urgent' },
    high: { order: 2, icon: 'AlertTriangle', color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', label: 'High' },
    medium: { order: 3, icon: 'Clock', color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30', label: 'Medium' },
    low: { order: 4, icon: 'ArrowDown', color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Low' }
  };

  // Status configuration
  const statusConfig = {
    todo: { order: 1, label: 'To Do', color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800' },
    'to do': { order: 1, label: 'To Do', color: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-100 dark:bg-slate-800' },
    in_progress: { order: 2, label: 'In Progress', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    'in progress': { order: 2, label: 'In Progress', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/30' },
    review: { order: 3, label: 'Review', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/30' },
    blocked: { order: 4, label: 'Blocked', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/30' },
    done: { order: 5, label: 'Done', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
    completed: { order: 5, label: 'Completed', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' }
  };

  // Check if task is completed
  const isCompleted = (status) => {
    const s = (status || '').toLowerCase();
    return s === 'done' || s === 'completed' || s === 'complete';
  };

  // Check if task is overdue
  const isOverdue = (task) => {
    if (!task.dueDate) return false;
    if (isCompleted(task.status)) return false;
    const dueDate = new Date(task.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    return dueDate < today;
  };

  // Calculate task stats
  const stats = useMemo(() => {
    const total = tasks.length;
    const completed = tasks.filter(t => isCompleted(t.status)).length;
    const inProgress = tasks.filter(t => {
      const s = (t.status || '').toLowerCase();
      return s === 'in_progress' || s === 'in progress';
    }).length;
    const overdue = tasks.filter(t => isOverdue(t)).length;
    const todo = tasks.filter(t => {
      const s = (t.status || '').toLowerCase();
      return s === 'todo' || s === 'to do' || s === 'to_do';
    }).length;

    return { total, completed, inProgress, overdue, todo };
  }, [tasks]);

  // Filter and sort tasks
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = [...tasks];

    // Apply filter
    switch (filter) {
      case 'overdue':
        filtered = filtered.filter(t => isOverdue(t));
        break;
      case 'in_progress':
        filtered = filtered.filter(t => {
          const s = (t.status || '').toLowerCase();
          return s === 'in_progress' || s === 'in progress';
        });
        break;
      case 'todo':
        filtered = filtered.filter(t => {
          const s = (t.status || '').toLowerCase();
          return s === 'todo' || s === 'to do' || s === 'to_do';
        });
        break;
      case 'completed':
        filtered = filtered.filter(t => isCompleted(t.status));
        break;
      default:
        // Remove completed tasks from 'all' by default for cleaner view
        filtered = filtered.filter(t => !isCompleted(t.status));
    }

    // Sort tasks
    filtered.sort((a, b) => {
      // Always put overdue first
      const aOverdue = isOverdue(a);
      const bOverdue = isOverdue(b);
      if (aOverdue && !bOverdue) return -1;
      if (!aOverdue && bOverdue) return 1;

      switch (sortBy) {
        case 'priority':
          const aPriority = priorityConfig[(a.priority || 'medium').toLowerCase()]?.order || 5;
          const bPriority = priorityConfig[(b.priority || 'medium').toLowerCase()]?.order || 5;
          return aPriority - bPriority;

        case 'dueDate':
          if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
          if (a.dueDate) return -1;
          if (b.dueDate) return 1;
          return 0;

        case 'status':
          const aStatus = statusConfig[(a.status || 'todo').toLowerCase()]?.order || 5;
          const bStatus = statusConfig[(b.status || 'todo').toLowerCase()]?.order || 5;
          return aStatus - bStatus;

        default:
          return 0;
      }
    });

    return filtered;
  }, [tasks, filter, sortBy]);

  // Format date for display
  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) return 'Today';
    if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
    if (date.getTime() === yesterday.getTime()) return 'Yesterday';
    
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  // Navigate to task
  const openTask = (task) => {
    if (task?.projectId) {
      navigate(`/project-management?id=${task.projectId}&tab=tasks`);
    } else {
      navigate('/project-management');
    }
  };

  // Get priority styles
  const getPriorityStyles = (priority) => {
    const config = priorityConfig[(priority || 'medium').toLowerCase()];
    return config || priorityConfig.medium;
  };

  // Get status styles
  const getStatusStyles = (status) => {
    const config = statusConfig[(status || 'todo').toLowerCase()];
    return config || statusConfig.todo;
  };

  // Filter button component
  const FilterButton = ({ value, label, count, icon }) => (
    <button
      onClick={() => setFilter(value)}
      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all duration-200
        ${filter === value 
          ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' 
          : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600'
        }`}
    >
      <Icon name={icon} size={14} />
      <span>{label}</span>
      <span className={`px-1.5 py-0.5 rounded-md text-[10px] ${filter === value ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-700'}`}>
        {count}
      </span>
    </button>
  );

  // Task card component
  const TaskCard = ({ task }) => {
    const overdue = isOverdue(task);
    const priorityStyle = getPriorityStyles(task.priority);
    const statusStyle = getStatusStyles(task.status);
    
    return (
      <button 
        onClick={() => openTask(task)} 
        className={`group w-full text-left p-5 rounded-2xl border transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl
          ${overdue 
            ? 'bg-rose-50/50 dark:bg-rose-900/10 border-rose-200 dark:border-rose-800/50 hover:border-rose-400' 
            : 'bg-white/70 dark:bg-slate-800/60 border-slate-100 dark:border-slate-700/50 hover:border-blue-400 dark:hover:border-blue-500'
          }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Priority Icon */}
            <div className={`w-12 h-12 ${priorityStyle.bg} rounded-xl flex items-center justify-center shadow-sm flex-shrink-0`}>
              <Icon name={priorityStyle.icon} size={20} className={priorityStyle.color} />
            </div>
            
            {/* Task Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                  {task.title}
                </h3>
                {overdue && (
                  <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400 flex-shrink-0">
                    Overdue
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                {/* Project */}
                {task.project && (
                  <div className="flex items-center gap-1.5">
                    <Icon name="Folder" size={12} className="text-slate-400" />
                    <span className="truncate max-w-[150px]">{task.project}</span>
                  </div>
                )}
                
                {/* Due Date */}
                {task.dueDate && (
                  <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-md ${overdue ? 'bg-rose-100 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400' : 'bg-slate-100 dark:bg-slate-800'}`}>
                    <Icon name="Calendar" size={12} className={overdue ? 'text-rose-400' : 'text-slate-400'} />
                    <span className="font-medium">{formatDueDate(task.dueDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Status & Arrow */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <span className={`hidden sm:inline-flex items-center px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusStyle.bg} ${statusStyle.color}`}>
              {statusStyle.label}
            </span>
            <Icon name="ChevronRight" size={20} className="text-slate-300 dark:text-slate-600 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
          </div>
        </div>
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30 dark:from-slate-950 dark:to-slate-900 relative overflow-hidden">
      {/* Aurora Background Effects */}
      <div className="absolute top-0 -left-64 w-[600px] h-[600px] bg-blue-400/10 dark:bg-blue-900/10 blur-[120px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 -right-64 w-[600px] h-[600px] bg-purple-400/10 dark:bg-purple-900/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative">
        <RoleBasedHeader />
        
        <main className="pt-24 pb-12 max-w-5xl mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
              My Tasks
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              {stats.total} total tasks • {stats.overdue > 0 && <span className="text-rose-500 font-medium">{stats.overdue} overdue</span>}
              {stats.overdue > 0 && ' • '}
              {stats.inProgress} in progress
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-100 dark:border-slate-700 shadow-sm">
              <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{stats.total}</div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total</div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-blue-100 dark:border-blue-900/50 shadow-sm">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.inProgress}</div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">In Progress</div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-rose-100 dark:border-rose-900/50 shadow-sm">
              <div className="text-2xl font-bold text-rose-600 dark:text-rose-400">{stats.overdue}</div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Overdue</div>
            </div>
            <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-emerald-100 dark:border-emerald-900/50 shadow-sm">
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{stats.completed}</div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Completed</div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <FilterButton value="all" label="Active" count={stats.total - stats.completed} icon="ListTodo" />
            <FilterButton value="overdue" label="Overdue" count={stats.overdue} icon="AlertCircle" />
            <FilterButton value="in_progress" label="In Progress" count={stats.inProgress} icon="Clock" />
            <FilterButton value="todo" label="To Do" count={stats.todo} icon="Circle" />
            <FilterButton value="completed" label="Completed" count={stats.completed} icon="CheckCircle" />
            
            {/* Sort dropdown */}
            <div className="ml-auto">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 rounded-xl text-xs font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 cursor-pointer focus:outline-none focus:border-blue-400"
              >
                <option value="priority">Sort by Priority</option>
                <option value="dueDate">Sort by Due Date</option>
                <option value="status">Sort by Status</option>
              </select>
            </div>
            
            {/* Refresh button */}
            <button 
              onClick={loadTasks}
              disabled={loading}
              className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-blue-400 transition-all disabled:opacity-50"
              title="Refresh tasks"
            >
              <Icon name="RefreshCw" size={16} className={`text-slate-500 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Task List */}
          <div className="bg-white/50 dark:bg-slate-800/30 backdrop-blur-sm rounded-2xl border border-slate-100 dark:border-slate-700/50 p-6">
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-20 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertTriangle" size={32} className="text-rose-500" />
                </div>
                <p className="text-rose-600 dark:text-rose-400 font-medium">{error}</p>
                <button onClick={loadTasks} className="mt-4 px-4 py-2 bg-rose-100 dark:bg-rose-900/20 rounded-lg text-rose-600 dark:text-rose-400 text-sm font-medium hover:bg-rose-200 transition-colors">
                  Try Again
                </button>
              </div>
            ) : filteredAndSortedTasks.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircle" size={40} className="text-emerald-500" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200 mb-2">
                  {filter === 'all' ? 'All Caught Up!' : `No ${filter.replace('_', ' ')} tasks`}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {filter === 'all' 
                    ? 'You have no pending tasks. Great job!' 
                    : 'Try changing the filter to see other tasks.'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAndSortedTasks.map(task => (
                  <TaskCard key={task.id} task={task} />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default MyTasksPage;
